import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAppSettings, resolveImageUrl } from "../context/AppSettingsContext";
import { useWaProvider } from "../lib/waProvider";
import api from "../lib/api";
import {
  Flame, LayoutDashboard, ScanLine, Users, Gift, Megaphone,
  UserCog, MessageCircle, LogOut, Menu, X, History, ClipboardList, Palette, BookOpen, Wrench, Layers,
  Ticket, Store, FileText, UsersRound, BarChart3, SearchCode, Package, Send, ShieldCheck, ShoppingBag,
  CreditCard, Cake, Sparkles, Gamepad2, Bell, Database, Newspaper, Wallet, PackageSearch, Tag, Plug, LayoutTemplate
} from "lucide-react";
import { Button } from "../components/ui/button";
import AdminNotifBell from "../components/AdminNotifBell";
import BillingBanner from "../components/BillingBanner";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { settings } = useAppSettings();
  const nav = useNavigate();
  const { label: waLabel } = useWaProvider();
  const [open, setOpen] = useState(false);
  const [badges, setBadges] = useState({ messages: 0, orders: 0, auth: 0 });
  const [tenantModules, setTenantModules] = useState({});
  useEffect(() => { api.get("/tenant/info").then(({ data }) => setTenantModules(data.modules || {})).catch(() => {}); }, []);
  const [sidebarCompact, setSidebarCompact] = useState(() => {
    // Bootstrap from localStorage; user.preferences takes over once /auth/me returns
    try { return localStorage.getItem("vivi:sidebarCompact") === "1"; }
    catch { return false; }
  });
  // Sync with server-side pref (survives across devices)
  useEffect(() => {
    if (user?.preferences && typeof user.preferences.sidebar_compact === "boolean") {
      setSidebarCompact(user.preferences.sidebar_compact);
      try { localStorage.setItem("vivi:sidebarCompact", user.preferences.sidebar_compact ? "1" : "0"); } catch {}
    }
  }, [user?.preferences]);
  const toggleCompact = () => {
    setSidebarCompact((v) => {
      const nx = !v;
      try { localStorage.setItem("vivi:sidebarCompact", nx ? "1" : "0"); } catch {}
      // Persist to server so state syncs across devices
      api.patch("/auth/prefs", { sidebar_compact: nx }).catch(() => {});
      return nx;
    });
  };
  const logoResolved = resolveImageUrl(settings.logo_url);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const loadBadges = async () => {
      try {
        const [m, o, a] = await Promise.all([
          api.get("/customer-messages/unread-count").then(r => r.data.count).catch(() => 0),
          api.get("/customer-orders", { params: { status_filter: "received" } }).then(r => r.data.length).catch(() => 0),
          api.get("/transactions/change-requests/pending-count").then(r => r.data.count).catch(() => 0),
        ]);
        setBadges({ messages: m, orders: o, auth: a });
      } catch {}
    };
    loadBadges();
    const t = setInterval(loadBadges, 20000);
    return () => clearInterval(t);
  }, [user?.role]);

  const links = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", perm: "view_reports" },
    { to: "/banco", label: "Negozio (POS)", icon: ScanLine, testid: "nav-counter", perm: "pos_earn_redeem" },
    { to: "/rilascio-card", label: "Rilascio Card", icon: CreditCard, testid: "nav-issue-card", perm: "pos_earn_redeem" },
    { to: "/codici-carta", label: "Codici Carta", icon: CreditCard, testid: "nav-card-codes", adminOnly: true },
    { to: "/clienti", label: "Clienti", icon: Users, testid: "nav-customers", perm: "manage_customers" },
    { to: "/ricerca", label: "Ricerca avanzata", icon: SearchCode, testid: "nav-search", perm: "manage_customers" },
    { to: "/statistiche", label: "Statistiche", icon: BarChart3, testid: "nav-stats", perm: "view_reports" },
    { to: "/movimenti", label: "Movimenti", icon: History, testid: "nav-transactions", perm: "view_reports" },
    { to: "/prenotazioni", label: "Prenotazioni", icon: ClipboardList, testid: "nav-reservations", perm: "manage_rewards", module: "reservations" },
    { to: "/gift-card", label: "Gift Card", icon: Gift, testid: "nav-giftcards" },
    { to: "/ordini-cliente", label: "Ordini Cliente", icon: ShoppingBag, testid: "nav-orders", badge: badges.orders, perm: "pos_earn_redeem", module: "orders" },
    { to: "/premi", label: "Catalogo Premi", icon: Gift, testid: "nav-rewards", perm: "manage_rewards" },
    { to: "/prodotti", label: "Prodotti", icon: Package, testid: "nav-products", perm: "manage_rewards" },
    { to: "/campagne", label: "Campagne", icon: Megaphone, testid: "nav-campaigns", perm: "manage_campaigns", module: "campaigns" },
    { to: "/raccolte", label: "Raccolte punti", icon: Layers, testid: "nav-collections", perm: "manage_rewards" },
    { to: "/coupon", label: "Coupon", icon: Ticket, testid: "nav-coupons", perm: "manage_coupons", module: "coupons" },
    { to: "/compleanni", label: "Compleanni & Anniv.", icon: Cake, testid: "nav-birthdays", perm: "manage_coupons", module: "birthdays" },
    { to: "/cluster", label: "Cluster Clienti", icon: UsersRound, testid: "nav-segments", perm: "manage_customers", module: "segments" },
    { to: "/report", label: "Report PDF", icon: FileText, testid: "nav-reports", perm: "view_reports", module: "reports" },
  ];
  const adminLinks = [
    { to: "/sedi", label: "Sedi", icon: Store, testid: "nav-branches" },
    { to: "/autorizzazioni", label: "Autorizzazioni", icon: ShieldCheck, testid: "nav-auth", badge: badges.auth },
    { to: "/messaggi", label: "Messaggi & News", icon: MessageCircle, testid: "nav-messages", badge: badges.messages },
    { to: "/volantini", label: "Volantini", icon: FileText, testid: "nav-flyers", perm: "manage_flyers", module: "flyers" },
    { to: "/home-cms", label: "Home pubblica (Banner & News)", icon: Newspaper, testid: "nav-home-cms", perm: "manage_settings", module: "cms" },
    { to: "/landing-cms", label: "Landing circuito", icon: LayoutTemplate, testid: "nav-landing-cms", perm: "manage_settings", module: "cms" },
    { to: "/comunicazioni", label: "Comunicazioni", icon: Send, testid: "nav-comm", perm: "manage_broadcast_send", module: "broadcast" },
    { to: "/staff", label: "Staff", icon: UserCog, testid: "nav-staff", perm: "manage_staff" },
    { to: "/whatsapp", label: "WhatsApp Hook", icon: MessageCircle, testid: "nav-whatsapp" },
    { to: "/spoki", label: `WhatsApp (${waLabel}) & SMS`, icon: MessageCircle, testid: "nav-spoki", module: "whatsapp" },
    { to: "/paypal", label: "PayPal (pagamenti ordini)", icon: Wallet, testid: "nav-paypal", adminOnly: true, module: ["orders", "paypal"] },
    { to: "/elastic", label: "CRM Elastic (import)", icon: Database, testid: "nav-elastic", adminOnly: true, module: "elastic" },
    { to: "/integrazioni-esterne", label: "API Esterne (punti)", icon: Plug, testid: "nav-extapi", adminOnly: true },
    { to: "/crm-dashboard", label: "Dashboard CRM", icon: BarChart3, testid: "nav-crm-dashboard", adminOnly: true, module: "elastic" },
    { to: "/crm-prodotti", label: "Analisi Prodotti CRM", icon: PackageSearch, testid: "nav-crm-products", adminOnly: true, module: ["elastic", "crm_products"] },
    { to: "/volantini-promo", label: "Analisi Volantini CRM", icon: Tag, testid: "nav-promo-flyers", adminOnly: true, module: ["elastic", "crm_flyers"] },
    { to: "/notifiche", label: "Notifiche admin", icon: Bell, testid: "nav-notifications", adminOnly: true },
    { to: "/assistente-ai", label: "Assistente AI", icon: Sparkles, testid: "nav-ai", module: "ai" },
    { to: "/concorsi", label: "Concorsi", icon: Gamepad2, testid: "nav-concorsi", module: "contest" },
    { to: "/sponsor", label: "Sponsor & Banner", icon: Megaphone, testid: "nav-sponsors", adminOnly: true, module: "sponsors" },
    { to: "/impostazioni", label: "Personalizza", icon: Palette, testid: "nav-settings", perm: "manage_settings" },
    { to: "/manuale", label: "Manuale", icon: BookOpen, testid: "nav-manual" },
    { to: "/manutenzione", label: "Manutenzione", icon: Wrench, testid: "nav-maintenance", adminOnly: true },
  ];

  // Filter links by permission: admin sees everything, staff sees only what
  // their `permissions` array allows. Links without a `perm` key are visible
  // to everyone (or gated at the endpoint level).
  const canSee = (l) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!l.perm) return false; // admin-only sections are hidden for non-admin
    return (user.permissions || []).includes(l.perm);
  };
  const moduleOn = (l) => !l.module || [].concat(l.module).every((m) => tenantModules[m] !== false);
  const visibleLinks = links.filter(canSee).filter(moduleOn);
  const visibleAdminLinks = adminLinks.filter((l) => user?.role === "admin" || (l.perm && (user?.permissions || []).includes(l.perm))).filter(moduleOn);

  const renderLink = (l) => (
    <NavLink
      key={l.to}
      to={l.to}
      end={l.to === "/"}
      data-testid={l.testid}
      onClick={() => setOpen(false)}
      title={sidebarCompact ? l.label : undefined}
      className={({ isActive }) => `side-link ${sidebarCompact ? "side-link-compact" : ""} ${isActive ? "active" : ""}`}
    >
      <l.icon className="h-4 w-4 shrink-0" />
      {!sidebarCompact && <span className="text-sm flex-1">{l.label}</span>}
      {l.badge > 0 && (
        <span
          data-testid={`${l.testid}-badge`}
          className={`bg-[color:var(--paprika)] text-white text-[10px] font-bold min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center animate-pulse ${sidebarCompact ? "absolute top-1 right-1 min-w-[14px] h-3.5 text-[8px] px-1" : "ml-auto"}`}
        >
          {l.badge > 99 ? "99+" : l.badge}
        </span>
      )}
    </NavLink>
  );

  const Sidebar = (
    <aside className={`vivi-sidebar ${sidebarCompact ? "w-20" : "w-72"} shrink-0 h-full flex flex-col transition-[width] duration-200`}>
      <div className={`${sidebarCompact ? "p-3 justify-center" : "p-5"} flex items-center gap-3 border-b border-[color:var(--border-dark)]`}>
        {logoResolved ? (
          <img src={logoResolved} alt="logo" className="h-10 w-10 rounded-xl object-cover ring-1 ring-white/10" />
        ) : (
          <div className="h-10 w-10 rounded-xl bg-[color:var(--paprika)] flex items-center justify-center glow-red">
            <Flame className="h-5 w-5" />
          </div>
        )}
        {!sidebarCompact && (
          <div className="min-w-0">
            <div className="font-heading font-bold text-sm tracking-wide truncate">{settings.app_name}</div>
            <div className="text-[11px] text-zinc-500 truncate">Console operatore</div>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {!sidebarCompact && <div className="px-2 pt-2 pb-1 text-[10px] font-semibold tracking-widest text-zinc-500">OPERAZIONI</div>}
        {visibleLinks.map(renderLink)}
        {visibleAdminLinks.length > 0 && (
          <>
            {!sidebarCompact && (
              <div className="px-2 pt-4 pb-1 text-[10px] font-semibold tracking-widest text-zinc-500">
                {user?.role === "admin" ? "AMMINISTRAZIONE" : "ALTRO"}
              </div>
            )}
            {sidebarCompact && <div className="border-t border-zinc-800 my-2" />}
            {visibleAdminLinks.map(renderLink)}
          </>
        )}
      </nav>
      <div className={`${sidebarCompact ? "p-2" : "p-3"} border-t border-[color:var(--border-dark)] hidden lg:block`}>
        <button
          type="button"
          onClick={toggleCompact}
          data-testid="sidebar-compact-toggle"
          className="w-full flex items-center justify-center gap-2 py-2 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-800/50 transition text-xs"
          title={sidebarCompact ? "Espandi sidebar" : "Comprimi sidebar (icone)"}
        >
          {sidebarCompact ? "▶" : "◀"}
          {!sidebarCompact && <span>Modalità compatta</span>}
        </button>
      </div>
      <div className={`${sidebarCompact ? "p-2" : "p-3"} border-t border-[color:var(--border-dark)]`}>
        <div className={`flex items-center gap-3 ${sidebarCompact ? "justify-center p-1" : "p-2"}`}>
          <div className="h-9 w-9 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-bold shrink-0" title={sidebarCompact ? user?.name : undefined}>
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          {!sidebarCompact && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider">{user?.role}</div>
            </div>
          )}
          {!sidebarCompact && (
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="icon"
              onClick={async () => { await logout(); nav("/login"); }}
              className="text-zinc-400 hover:text-white"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
        {sidebarCompact && (
          <Button
            data-testid="logout-button"
            variant="ghost"
            size="icon"
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-full mt-1 text-zinc-400 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        )}
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-[color:var(--bg-dark)] text-zinc-100">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex sticky top-0 h-screen">{Sidebar}</div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative z-50" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="mobile-drawer-close"
              aria-label="Chiudi menu"
              className="absolute top-3 right-3 z-10 p-1.5 rounded-md bg-zinc-800/80 text-zinc-300 hover:text-white hover:bg-zinc-700 transition"
            >
              <X className="h-4 w-4" />
            </button>
            {Sidebar}
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col pb-16 lg:pb-0">
        <header className="lg:hidden sticky top-0 z-30 backdrop-blur-md bg-zinc-900/80 border-b border-[color:var(--border-dark)] px-4 py-3 flex items-center justify-between">
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            className="p-2 rounded-md bg-zinc-800/60"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="flex items-center gap-2">
            {logoResolved ? (
              <img src={logoResolved} alt="logo" className="h-6 w-6 rounded object-cover" />
            ) : (
              <Flame className="h-4 w-4 text-[color:var(--paprika)]" />
            )}
            <span className="font-heading font-bold text-sm truncate max-w-[180px]">{settings.app_name}</span>
          </div>
          <AdminNotifBell variant="mobile" />
        </header>
        <div className="hidden lg:flex justify-end px-8 pt-4 -mb-6" data-testid="desktop-topbar">
          <AdminNotifBell />
        </div>
        <BillingBanner isAdmin={user?.role === "admin"} />
        <div className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav (visible only <lg) */}
      <nav className="mobile-bottom-nav lg:hidden" data-testid="mobile-bottom-nav">
        {(() => {
          const configured = Array.isArray(settings.mobile_bottom_nav_slots) && settings.mobile_bottom_nav_slots.length > 0
            ? settings.mobile_bottom_nav_slots
            : ["/banco", "/ordini-cliente", "/clienti"];
          const allLinks = [...links, ...adminLinks];
          const slots = configured.slice(0, 3).map((path, idx) => {
            const link = allLinks.find((l) => l.to === path);
            if (!link) return null;
            return { ...link, testid: `botnav-slot-${idx}` };
          }).filter(Boolean);
          return slots.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) => isActive ? "active" : ""}
            >
              <div className="relative">
                <l.icon className="h-5 w-5" />
                {l.badge > 0 && (
                  <span
                    data-testid={`${l.testid}-badge`}
                    className="absolute -top-1 -right-2 bg-[color:var(--paprika)] text-white text-[9px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center"
                  >
                    {l.badge > 99 ? "99+" : l.badge}
                  </span>
                )}
              </div>
              <span className="truncate max-w-full">{l.label}</span>
            </NavLink>
          ));
        })()}
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="botnav-menu"
          aria-label="Apri menu"
          className="relative"
        >
          <div className="relative">
            <Menu className="h-5 w-5" />
            {(badges.messages + badges.auth) > 0 && (
              <span
                data-testid="botnav-menu-badge"
                className="absolute -top-1 -right-2 bg-[color:var(--paprika)] text-white text-[9px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center"
                title={`${badges.messages} messaggi · ${badges.auth} autorizzazioni`}
              >
                {(badges.messages + badges.auth) > 99 ? "99+" : (badges.messages + badges.auth)}
              </span>
            )}
          </div>
          <span>Menu</span>
        </button>
      </nav>
    </div>
  );
}
